import { useTranslation } from "react-i18next";
import { Card } from "../../../components/Card";
import { SectionTitle } from "../../../components/SectionTitle";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "../../../components/Table";
import { StoreSearchPicker } from "../StoreSearchPicker";
import { formatMoney } from "../../../utils/formatMoney";
import type { StoreSearchKeyResponse } from "../../../api/storesApi";
import {
  KICKBACK_REPORT_SOURCES,
  type KickbackReportSource,
} from "@KassaroAB/shared/types/enums/KickbackSource";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import type { RestKickbackSourceReport } from "@KassaroAB/shared/types/invoices/kickback";

const isReportSource = (source: string): source is KickbackReportSource =>
  KICKBACK_REPORT_SOURCES.some((reportSource) => reportSource === source);

export const KickbackUnmatchedTable = ({
  reports,
  searchConfig,
  disabled,
  onMatch,
}: {
  reports: RestKickbackSourceReport[];
  searchConfig: StoreSearchKeyResponse | null;
  disabled: boolean;
  onMatch: (source: KickbackReportSource, reference: string, storeId: StoreId) => void;
}) => {
  const { t } = useTranslation();
  const unmatchedRows = reports.flatMap((report) =>
    report.rows
      .filter((row) => row.resellerId === null)
      .map((row) => ({ source: report.source, row }))
  );

  if (unmatchedRows.length === 0) {
    return null;
  }

  return (
    <Card>
      <SectionTitle>{t("kickback.unmatchedTitle")}</SectionTitle>
      <p>{t("kickback.unmatchedDescription")}</p>
      <Table>
        <TableHeader>
          <tr>
            <TableHeaderCell>{t("kickback.sourceColumn")}</TableHeaderCell>
            <TableHeaderCell>{t("kickback.reference")}</TableHeaderCell>
            <TableHeaderCell>{t("kickback.matchedTo")}</TableHeaderCell>
            <TableHeaderCell align="right">{t("kickback.grossAmount")}</TableHeaderCell>
            <TableHeaderCell>{t("kickback.match")}</TableHeaderCell>
          </tr>
        </TableHeader>
        <TableBody>
          {unmatchedRows.map(({ source, row }) => (
            <TableRow key={`${source}-${row.reference}`}>
              <TableCell noWrap>{t(`kickback.source.${source}`)}</TableCell>
              <TableCell>{row.reference}</TableCell>
              <TableCell>
                {row.storeName ?? row.chainName ?? t("kickback.noMatch")}
                {(row.storeName ?? row.chainName) && ` (${t("kickback.noReseller")})`}
              </TableCell>
              <TableCell align="right" noWrap>
                {formatMoney(row.grossAmount)}
              </TableCell>
              <TableCell>
                {isReportSource(source) ? (
                  <StoreSearchPicker
                    searchConfig={searchConfig}
                    value={null}
                    disabled={disabled}
                    onChange={(store) => {
                      if (store) {
                        onMatch(source, row.reference, store.id);
                      }
                    }}
                  />
                ) : (
                  t("kickback.vivaUnmatchedHint")
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};
